import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { formatVendorStartingPrice } from '../utils/pricing';
import { getVendorAvatar } from '../utils/vendorImage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CeleyLoader } from '../components/ui/CeleyLoader';
import TimePicker from '../components/ui/TimePicker';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Sparkles, MapPin, DollarSign, Globe, Instagram, Facebook, MessageCircle, Share2, Copy, Check, X, Star, MessageSquareText, AlertTriangle, Clock, ArrowLeft, Camera, Tag, CheckCircle2, CalendarCheck, Zap, ChevronLeft, ChevronRight, Heart, Image as ImageIcon, Plus, CheckCircle, ShieldCheck, Eye } from 'lucide-react';
import Navigation from '../components/Navigation';
import BottomTabBar from '../components/BottomTabBar';
import Footer from '../components/Footer';
import { BadgeList } from '../components/BadgeDisplay';
import ComposerSheet from '../components/ui/ComposerSheet';
import { openShareUrl } from '../utils/navigation';
import ImageLightbox from '../components/ImageLightbox';
import { getServiceAreaDisplay } from '../utils/vendorDisplay';
import { getHeroImage } from '../utils/vendorImage';

// Direct Inquiry fallback event types — used only when the canonical list
// fetch from `GET /api/event-types` fails. Keeps the modal usable even on
// flaky networks. Mirrors the previous hardcoded list.
const DIRECT_INQUIRY_FALLBACK_TYPES = [
  'Birthday Party (Kids)',
  'Birthday Party (Adults)',
  'Wedding',
  'Baby Shower',
  'Engagement Party',
  'Graduation Party',
  'Corporate Event',
  'Holiday Party',
  'Community Event',
];

// "Other (Please Describe)" sentinel — always rendered as the final option.
const OTHER_EVENT_TYPE = 'Other (Please Describe)';

const BioSection = ({ bio }) => {
  const [expanded, setExpanded] = React.useState(false);
  const needsExpand = bio.length > 200;
  return (
    <div className="bg-white rounded-2xl border border-[#ECECEA] p-4" data-testid="vendor-bio-section">
      <h3 className="text-[14px] font-bold text-[#1A1A1A] mb-1.5">About</h3>
      <p className={`text-[13px] text-[#4A4A45] leading-relaxed ${!expanded && needsExpand ? 'line-clamp-4' : ''}`} data-testid="vendor-bio">{bio}</p>
      {needsExpand && (
        <button onClick={() => setExpanded(v => !v)} className="text-[12px] font-medium text-emerald-600 hover:text-emerald-700 mt-1" data-testid="bio-read-more">
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}
    </div>
  );
};

const INCLUDES_VISIBLE_COUNT = 4;

const PackageIncludes = ({ items }) => {
  const [expanded, setExpanded] = React.useState(false);
  const hasMore = items.length > INCLUDES_VISIBLE_COUNT;
  const visible = expanded ? items : items.slice(0, INCLUDES_VISIBLE_COUNT);
  return (
    <div className="mt-3 pt-3 border-t border-gray-50">
      <p className="text-[11px] font-semibold text-[#8A8A82] uppercase tracking-wider mb-2">What&apos;s Included</p>
      <div className="space-y-1.5">
        {visible.map((item, i) => (
          <div key={i} className="flex items-start gap-1.5 text-[12px] text-[#4A4A45]">
            <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0 mt-0.5" />
            <span className="line-clamp-2">{item}</span>
          </div>
        ))}
      </div>
      {hasMore && (
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}
          className="text-[11px] font-medium text-emerald-600 hover:text-emerald-700 mt-2"
          data-testid="package-show-more"
        >
          {expanded ? 'Show less' : `+${items.length - INCLUDES_VISIBLE_COUNT} more`}
        </button>
      )}
    </div>
  );
};

const VendorDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, logout, activeRole } = useAuth();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  // Guards against duplicate "Vendor not found" toasts and redirects when
  // the parent useEffect re-fires (e.g. `user` object reference changes from
  // AuthContext, React StrictMode double-mount in dev). Reset on id change.
  const notFoundShownRef = useRef(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [submittingInquiry, setSubmittingInquiry] = useState(false);
  // Ref-based lock: React state updates are batched and not synchronous,
  // so a rapid double-tap reads the old `submittingInquiry === false`
  // closure value before the first re-render. The ref is mutated
  // synchronously inside the handler so the second tap sees `true` and
  // bails immediately. This guarantees one tap → one POST.
  const submittingInquiryRef = useRef(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [copied, setCopied] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [eventData, setEventData] = useState(null); // Event context data for pre-filling
  const [loadingEventData, setLoadingEventData] = useState(false);
  const [userEvents, setUserEvents] = useState([]); // User's events for optional attachment
  const [loadingUserEvents, setLoadingUserEvents] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null); // Selected event for attachment
  const [hasUserClearedEventAttachment, setHasUserClearedEventAttachment] = useState(false); // Blocks single-event auto-attach after explicit Clear; reset on modal open/close/submit
  const [inquiryData, setInquiryData] = useState({
    event_type: '',
    event_date: '',
    event_time: '',
    location: '',
    guest_count: '',
    budget_range: '',
    message: ''
  });
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [vendorBadges, setVendorBadges] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [reviewFilter, setReviewFilter] = useState('all');
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [existingInquiry, setExistingInquiry] = useState(null);
  // Canonical event-type list fetched from backend; falls back to a small
  // hardcoded list (see DIRECT_INQUIRY_FALLBACK_TYPES below) when the
  // network call fails so the modal never breaks.
  const [eventTypeOptions, setEventTypeOptions] = useState([]);
  const headerCtaRef = useRef(null);
  
  // Event context - if viewing from an event, we'll return there after sending inquiry
  const eventId = searchParams.get('eventId');
  const returnToEvent = searchParams.get('returnToEvent') === 'true';
  const fromPlan = searchParams.get('fromPlan') === 'true';
  const planEventType = searchParams.get('planEventType') || '';
  
  // Browse context — show "Back to Browse" on desktop when no other context banner is active
  const fromBrowse = searchParams.get('fromBrowse') === 'true';

  // Fetch event data when browsing from an event context
  useEffect(() => {
    if (eventId && returnToEvent) {
      fetchEventData();
    }
  }, [eventId, returnToEvent]);

  // Fetch user's events when inquiry dialog opens (for optional attachment)
  useEffect(() => {
    if (inquiryOpen && !eventId && user) {
      fetchUserEvents();
    }
  }, [inquiryOpen, eventId, user]);

  // Event Context Contract: auto-attach when the customer has exactly one
  // active event. Removes the confusing pre-selection state where a single
  // event card looks attached but the required form fields remain blank.
  // Only fires while the modal is open, no event is yet selected, the user
  // has NOT explicitly cleared during this modal session, and we are not in
  // the eventId URL-context path (which has its own hydration via
  // fetchEventData).
  useEffect(() => {
    if (
      inquiryOpen &&
      !eventId &&
      !selectedEventId &&
      !hasUserClearedEventAttachment &&
      userEvents.length === 1
    ) {
      handleSelectEvent(userEvents[0]);
    }
  }, [inquiryOpen, eventId, selectedEventId, userEvents, hasUserClearedEventAttachment]);

  // Reset the "user cleared" sentinel whenever the inquiry modal transitions
  // (open ↔ closed). A fresh modal session is allowed to auto-attach again.
  useEffect(() => {
    setHasUserClearedEventAttachment(false);
  }, [inquiryOpen]);

  // Canonical event-type list — same source as the Plan Event flow
  // (`backend/routes/events.py:DEFAULT_EVENT_TYPES` via GET /event-types).
  // Falls back to DIRECT_INQUIRY_FALLBACK_TYPES on any failure so the modal
  // remains usable offline / on flaky networks. No enum enforced; backend
  // stores `event_type` as free-form.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/event-types`);
        const types = Array.isArray(res?.data?.types) ? res.data.types : [];
        if (!cancelled && types.length > 0) {
          setEventTypeOptions(types);
          return;
        }
        throw new Error('empty list');
      } catch {
        if (!cancelled) setEventTypeOptions(DIRECT_INQUIRY_FALLBACK_TYPES);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleServiceSelection = (name) => {
    setSelectedServices(prev => 
      prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name]
    );
  };

  const saveAuthRedirect = () => {
    localStorage.setItem('authRedirect', window.location.pathname + window.location.search);
  };

  const openInquiryWithSelection = () => {
    if (!user) { toast.error('Please sign in'); saveAuthRedirect(); navigate('/auth'); return; }
    if (activeRole === 'vendor') { toast.error('Switch to Customer mode to send inquiries.'); return; }
    setInquiryOpen(true);
  };

  const fetchEventData = async () => {
    setLoadingEventData(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const event = response.data;
      setEventData(event);
      
      // Pre-fill inquiry form with event data
      const budgetRangeFromEvent = event.budget 
        ? (event.budget < 250 ? 'Under $250' 
           : event.budget < 500 ? '$250-$500'
           : event.budget < 1000 ? '$500-$1000'
           : '$1000+')
        : '';
      
      // Build location string from city/state/zip
      const locationParts = [];
      if (event.location_city) locationParts.push(event.location_city);
      if (event.location_state) locationParts.push(event.location_state);
      if (event.location_zip) locationParts.push(event.location_zip);
      const locationString = locationParts.join(', ');
      
      // Map simple event types to full event type options
      const eventTypeMapping = {
        'birthday': 'Birthday Party (Kids)',
        'wedding': 'Wedding',
        'baby_shower': 'Baby Shower',
        'corporate': 'Corporate Event',
        'graduation': 'Graduation Party',
        'church_event': 'Church Event',
        'other': 'Other (Please Describe)',
        'Birthday': 'Birthday Party (Kids)',
        'Wedding': 'Wedding',
        'Baby Shower': 'Baby Shower',
        'Corporate Event': 'Corporate Event',
        'Graduation': 'Graduation Party',
        'Anniversary': 'Anniversary',
        'Holiday Party': 'Holiday Party',
        'Other': 'Other (Please Describe)',
        'Birthday Party': 'Birthday Party (Kids)',
        'Graduation Party': 'Graduation Party',
        'Church Event': 'Church Event',
      };
      const mappedEventType = eventTypeMapping[event.event_type] || event.event_type || '';
      
      setInquiryData(prev => ({
        ...prev,
        event_type: mappedEventType,
        event_date: event.event_date ? event.event_date.split('T')[0] : '',
        event_time: event.event_time || '',
        location: locationString,
        guest_count: event.guest_count ? String(event.guest_count) : '',
        budget_range: budgetRangeFromEvent,
        message: prev.message || '' // Keep any existing message
      }));
    } catch (error) {
      console.error('Error fetching event data:', error);
      // Don't show error - just proceed without pre-fill
    } finally {
      setLoadingEventData(false);
    }
  };

  // Fetch user's events for optional attachment (when not coming from event context)
  const fetchUserEvents = async () => {
    if (!user) return;
    setLoadingUserEvents(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/events/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to only show active events (not cancelled or completed)
      const activeEvents = response.data.filter(e => 
        ['draft', 'planning', 'booked', 'final'].includes(e.status)
      );
      setUserEvents(activeEvents);
    } catch (error) {
      console.error('Error fetching user events:', error);
    } finally {
      setLoadingUserEvents(false);
    }
  };

  // Handle selecting an event to attach inquiry to
  const handleSelectEvent = (event) => {
    setSelectedEventId(event.id);
    setEventData(event);
    
    // Pre-fill form with selected event data
    const budgetRangeFromEvent = event.budget 
      ? (event.budget < 250 ? 'Under $250' 
         : event.budget < 500 ? '$250-$500'
         : event.budget < 1000 ? '$500-$1000'
         : '$1000+')
      : '';
    
    const locationParts = [];
    if (event.location_city) locationParts.push(event.location_city);
    if (event.location_state) locationParts.push(event.location_state);
    if (event.location_zip) locationParts.push(event.location_zip);
    const locationString = locationParts.join(', ');
    
    const eventTypeMapping = {
      'birthday': 'Birthday Party (Kids)',
      'wedding': 'Wedding',
      'baby_shower': 'Baby Shower',
      'corporate': 'Corporate Event',
      'graduation': 'Graduation Party',
      'church_event': 'Church Event',
      'other': 'Other (Please Describe)',
      'Birthday': 'Birthday Party (Kids)',
      'Wedding': 'Wedding',
      'Baby Shower': 'Baby Shower',
      'Corporate Event': 'Corporate Event',
      'Graduation': 'Graduation Party',
      'Anniversary': 'Anniversary',
      'Holiday Party': 'Holiday Party',
      'Other': 'Other (Please Describe)',
      'Birthday Party': 'Birthday Party (Kids)',
      'Graduation Party': 'Graduation Party',
      'Church Event': 'Church Event',
    };
    const mappedEventType = eventTypeMapping[event.event_type] || event.event_type || '';
    
    setInquiryData(prev => ({
      ...prev,
      event_type: mappedEventType,
      event_date: event.event_date ? event.event_date.split('T')[0] : '',
      event_time: event.event_time || '',
      location: locationString,
      guest_count: event.guest_count ? String(event.guest_count) : '',
      budget_range: budgetRangeFromEvent,
      message: prev.message || ''
    }));
    
    toast.success(`Linked to "${event.event_type}" event`);
  };

  // Clear event attachment
  const handleClearEventAttachment = () => {
    setSelectedEventId(null);
    setEventData(null);
    // User intent wins: block single-event auto-attach from firing again
    // during this same modal session. Sentinel resets when the modal opens
    // or closes (see effect above) and after a successful submit (see
    // handleInquirySubmit).
    setHasUserClearedEventAttachment(true);
    // Reset form fields
    setInquiryData({
      event_type: '',
      event_date: '',
      event_time: '',
      location: '',
      guest_count: '',
      budget_range: '',
      message: inquiryData.message // Keep the message
    });
  };

  // Helper function to get full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath || imagePath.trim() === '') return '';
    // Return external URLs as-is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    // For local uploads, prepend backend URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    // Ensure path starts with /
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${backendUrl}${cleanPath}`;
  };

  useEffect(() => {
    // Reset the duplicate-toast guard whenever the vendor id changes.
    notFoundShownRef.current = false;
    fetchVendor();
    fetchReviews();
    fetchVendorBadges();
    
    // Store last customer route when viewing vendor detail page
    // This is part of customer browse flow, so only store for customer role
    if (activeRole === 'customer' || (!activeRole && user?.role === 'customer')) {
      localStorage.setItem('celey:lastRoute:customer', `/vendors/${id}`);
    }
  }, [id, activeRole, user]);

  // Check if the logged-in customer already has an inquiry with this vendor
  useEffect(() => {
    const checkExistingInquiry = async () => {
      if (!user || (activeRole === 'vendor')) {
        setExistingInquiry(null);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/inquiries/customer`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const match = response.data.find(inq => inq.vendor_id === id);
        setExistingInquiry(match || null);
      } catch {
        setExistingInquiry(null);
      }
    };
    checkExistingInquiry();
  }, [id, user, activeRole]);

  const fetchVendorBadges = async () => {
    try {
      const response = await axios.get(`${API}/vendors/${id}/badges`);
      setVendorBadges(response.data);
    } catch (error) {
      console.error('Error fetching vendor badges:', error);
    }
  };

  const fetchVendor = async () => {
    try {
      // Send auth header when a token is present so the backend can detect
      // the vendor owner and grant owner-preview access to unpublished
      // profiles. Anonymous and non-owner authenticated callers still
      // receive 404 — public visibility rules are unchanged.
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/vendors/${id}`, { headers });
      setVendor(response.data);
      setBlockedDates((response.data.blocked_dates || []).map(d => d));
    } catch (error) {
      console.error('Error fetching vendor:', error);
      // Guard against duplicate toasts when this effect re-runs (the parent
      // `useEffect` depends on the `user` object reference, which can change
      // across AuthContext re-renders / StrictMode double-mount in dev).
      if (notFoundShownRef.current) return;
      notFoundShownRef.current = true;
      toast.error('Vendor not found');
      navigate('/browse');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/vendors/${id}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  // Calculate average rating
  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  // Rating breakdown (5-star to 1-star counts)
  const ratingBreakdown = useMemo(() => {
    const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(r => { if (counts[r.rating] !== undefined) counts[r.rating]++; });
    return counts;
  }, [reviews]);

  // Unique event types from reviews for filter chips
  const reviewEventTypes = useMemo(() => {
    const types = new Set();
    reviews.forEach(r => { if (r.event_type) types.add(r.event_type); });
    return Array.from(types);
  }, [reviews]);

  // Filtered reviews
  const filteredReviews = useMemo(() => {
    if (reviewFilter === 'all') return reviews;
    return reviews.filter(r => r.event_type === reviewFilter);
  }, [reviews, reviewFilter]);

  // Specialty chips derived from vendor categories (with emojis like the reference design)
  const specialtyChips = useMemo(() => {
    if (!vendor) return [];
    const chipMap = {
      'Photography': [{ label: 'Weddings', icon: '💒' }, { label: 'Portraits', icon: '📸' }, { label: 'Events', icon: '🎉' }],
      'DJ': [{ label: 'Weddings', icon: '💒' }, { label: 'Parties', icon: '🎉' }, { label: 'Corporate', icon: '🏢' }],
      'Catering': [{ label: 'Full Service', icon: '🍽️' }, { label: 'Buffet', icon: '🥘' }, { label: 'Plated', icon: '🍛' }],
      'Balloon Decor': [{ label: 'Kids Parties', icon: '🎈' }, { label: 'Baby Showers', icon: '👶' }, { label: 'Themed Backdrops', icon: '🎊' }],
      'Event Decor': [{ label: 'Themed Backdrops', icon: '🎊' }, { label: 'Table Settings', icon: '🍽️' }, { label: 'Centerpieces', icon: '💐' }],
      'Bounce House': [{ label: 'Kids Parties', icon: '🎈' }, { label: 'Outdoor Events', icon: '☀️' }],
      'Face Painting': [{ label: 'Kids Parties', icon: '🎈' }, { label: 'Festivals', icon: '🎭' }],
      'Florist': [{ label: 'Weddings', icon: '💒' }, { label: 'Centerpieces', icon: '💐' }, { label: 'Bouquets', icon: '🌸' }],
      'Bakery': [{ label: 'Custom Cakes', icon: '🎂' }, { label: 'Dessert Tables', icon: '🧁' }, { label: 'Cupcakes', icon: '🧁' }],
      'Videography': [{ label: 'Weddings', icon: '💒' }, { label: 'Events', icon: '🎉' }, { label: 'Highlights', icon: '🎬' }],
      'Entertainment': [{ label: 'Live Music', icon: '🎵' }, { label: 'Performers', icon: '🎭' }, { label: 'Comedy', icon: '😂' }],
      'Rentals': [{ label: 'Tables & Chairs', icon: '🪑' }, { label: 'Linens', icon: '🧵' }, { label: 'Tents', icon: '⛺' }],
      'Photo Booth': [{ label: 'Weddings', icon: '💒' }, { label: 'Parties', icon: '🎉' }, { label: 'Corporate', icon: '🏢' }],
      'Planner': [{ label: 'Full Planning', icon: '📋' }, { label: 'Day-of Coordination', icon: '⏰' }, { label: 'Design', icon: '✨' }],
    };
    const seen = new Set();
    const chips = [];
    (vendor.categories || []).forEach(cat => {
      const mapped = chipMap[cat];
      if (mapped) mapped.forEach(c => { if (!seen.has(c.label)) { seen.add(c.label); chips.push(c); } });
      else if (!seen.has(cat)) { seen.add(cat); chips.push({ label: cat, icon: '✨' }); }
    });
    return chips.slice(0, 6);
  }, [vendor]);

  // Portfolio photos (filtered valid ones)
  const validPhotos = useMemo(() => {
    if (!vendor) return [];
    return (vendor.portfolio_photos || []).filter(p => p && p.trim() !== '');
  }, [vendor]);

  // All gallery images for lightbox (cover + profile + hero + portfolio)
  const allGalleryImages = useMemo(() => {
    if (!vendor) return [];
    const imgs = [];
    // Vendor-selected hero (cover_image) is always first when set
    if (vendor.cover_image && vendor.cover_image.trim() !== '') {
      imgs.push(getImageUrl(vendor.cover_image));
    }
    if (vendor.profile_photo) {
      const url = getImageUrl(vendor.profile_photo);
      if (!imgs.includes(url)) imgs.push(url);
    }
    validPhotos.forEach(p => {
      const url = getImageUrl(p);
      if (!imgs.includes(url)) imgs.push(url);
    });
    return imgs;
  }, [vendor, validPhotos]);

  // Helper to open lightbox at a specific image URL
  const openLightbox = useCallback((imageUrl) => {
    const idx = allGalleryImages.indexOf(imageUrl);
    setLightboxIndex(idx >= 0 ? idx : 0);
    setLightboxImages(allGalleryImages);
    setLightboxOpen(true);
  }, [allGalleryImages]);

  // Open lightbox for review images (separate image set)
  const openReviewLightbox = useCallback((reviewImages, clickedIdx) => {
    const urls = reviewImages.map(img => getImageUrl(img));
    setLightboxImages(urls);
    setLightboxIndex(clickedIdx);
    setLightboxOpen(true);
  }, []);

  // Structured services with fallback to categories
  const displayServices = useMemo(() => {
    if (!vendor) return [];
    if (vendor.services && vendor.services.length > 0) return vendor.services;
    // Fallback: generate from categories + starting_price
    return (vendor.categories || []).map((cat, idx) => ({
      type: 'service',
      name: cat,
      is_primary: vendor.primary_service === cat || (!vendor.primary_service && idx === 0),
      starting_price: Array.isArray(vendor.starting_price) && vendor.starting_price[idx]
        ? vendor.starting_price[idx]
        : (Array.isArray(vendor.starting_price) && vendor.starting_price[0])
          ? vendor.starting_price[0]
          : vendor.starting_price || null,
      available: true,
    }));
  }, [vendor]);

  // Split into packages and individual services
  const packages = useMemo(() => displayServices.filter(s => s.type === 'package'), [displayServices]);
  const individualServices = useMemo(() => displayServices.filter(s => s.type !== 'package'), [displayServices]);
  const [servicesViewTab, setServicesViewTab] = useState(() => 'all');

  // Scroll-based CTA visibility
  useEffect(() => {
    const handleScroll = () => {
      if (!headerCtaRef.current) return;
      const rect = headerCtaRef.current.getBoundingClientRect();
      setShowStickyCta(rect.bottom < 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Render stars for reviews
  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const isDateBlocked = (dateStr) => {
    return blockedDates.includes(dateStr);
  };

  // Validate date against vendor's booking rules
  const validateBookingDate = (dateStr) => {
    if (!dateStr || !vendor) return { valid: true, error: '' };
    
    const eventDate = new Date(dateStr + 'T12:00:00'); // Noon to avoid timezone issues
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const eventDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
    
    const daysDiff = Math.floor((eventDay - today) / (1000 * 60 * 60 * 24));
    
    // Check blocked dates
    if (blockedDates.includes(dateStr)) {
      return { valid: false, error: 'This date is unavailable' };
    }
    
    // Get vendor's booking rules (with defaults)
    const allowSameDay = vendor.allow_same_day || false;
    const allowNextDay = vendor.allow_next_day || false;
    const minNoticeHours = vendor.min_notice_hours || 0;
    const minNoticeDays = Math.ceil(minNoticeHours / 24);
    
    // Same-day check
    if (daysDiff === 0) {
      if (!allowSameDay) {
        return { valid: false, error: 'This vendor does not accept same-day bookings' };
      }
      // Check cutoff time
      const cutoffHour = vendor.same_day_cutoff_hour || 15;
      const cutoffMinute = vendor.same_day_cutoff_minute || 0;
      if (now.getHours() > cutoffHour || (now.getHours() === cutoffHour && now.getMinutes() >= cutoffMinute)) {
        return { valid: false, error: `Same-day bookings must be made before ${cutoffHour}:${cutoffMinute.toString().padStart(2, '0')}` };
      }
      return { valid: true, error: '' };
    }
    
    // Next-day check
    if (daysDiff === 1) {
      if (allowNextDay) {
        return { valid: true, error: '' };
      }
      // Fall through to min notice check
    }
    
    // Minimum notice check
    if (minNoticeHours > 0 && daysDiff < minNoticeDays) {
      return { valid: false, error: `This vendor requires at least ${minNoticeDays} day${minNoticeDays > 1 ? 's' : ''} notice` };
    }
    
    return { valid: true, error: '' };
  };

  // Get minimum allowed date for the date picker
  const getMinDate = () => {
    if (!vendor) return new Date().toISOString().split('T')[0];
    
    const now = new Date();
    const minNoticeHours = vendor.min_notice_hours || 0;
    const minNoticeDays = Math.ceil(minNoticeHours / 24);
    
    // If same-day is allowed, today is the min
    if (vendor.allow_same_day) {
      return now.toISOString().split('T')[0];
    }
    
    // If next-day is allowed, tomorrow is the min
    if (vendor.allow_next_day) {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split('T')[0];
    }
    
    // Otherwise, use min notice days
    const minDate = new Date(now);
    minDate.setDate(minDate.getDate() + Math.max(1, minNoticeDays));
    return minDate.toISOString().split('T')[0];
  };

  const handleInquirySubmit = async (e) => {
    if (e) e.preventDefault();
    
    // Submit lock — prevents the double-tap reentrancy that previously
    // produced two POST /inquiries (→ two backend pushes + two success
    // toasts). React state is async/batched, so we use a synchronous
    // ref to block the second tap inside the same render tick.
    // One tap → one inquiry → one toast → one push.
    if (submittingInquiryRef.current) return;
    submittingInquiryRef.current = true;
    
    if (!user) {
      toast.error('Please sign in to send an inquiry');
      saveAuthRedirect();
      navigate('/auth');
      submittingInquiryRef.current = false;
      return;
    }

    // Required-field validation — fields display required asterisks in the
    // form but ComposerSheet does not wrap children in a <form>, so HTML5
    // `required` never fires. Guard manually so blank submissions can't
    // reach the backend.
    const requiredFields = [
      { key: 'event_type', label: 'Event Type' },
      { key: 'event_date', label: 'Event Date' },
      { key: 'location', label: 'Event Location' },
      { key: 'message', label: 'Message' },
    ];
    const missing = requiredFields.find((f) => !String(inquiryData[f.key] || '').trim());
    if (missing) {
      toast.error(`Please fill in ${missing.label}`);
      try {
        const el = document.getElementById(missing.key);
        if (el && typeof el.focus === 'function') el.focus();
      } catch { /* focus best-effort */ }
      submittingInquiryRef.current = false;
      return;
    }

    // Validate date against vendor's booking rules
    const dateValidation = validateBookingDate(inquiryData.event_date);
    if (!dateValidation.valid) {
      toast.error(dateValidation.error);
      submittingInquiryRef.current = false;
      return;
    }

    setSubmittingInquiry(true);
    try {
      const token = localStorage.getItem('token');
      
      // Build inquiry payload - include event_id if browsing from an event OR selected an event to attach
      const effectiveEventId = eventId || selectedEventId;
      const inquiryPayload = { 
        ...inquiryData, 
        vendor_id: id,
        ...(effectiveEventId ? { event_id: effectiveEventId } : {}),
        ...(selectedServices.length > 0 ? { selected_package: selectedServices.join(', ') } : {})
      };
      
      await axios.post(
        `${API}/inquiries`,
        inquiryPayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Show different message if vendor listing is inactive
      if (vendor.vendor_active === false || vendor.admin_visible === false) {
        toast.success(
          "Your message was sent! This provider's listing is currently inactive. Your conversation may appear once they reactivate their listing.",
          { duration: 6000 }
        );
      } else {
        // Show different success message based on event attachment
        if (effectiveEventId) {
          toast.success('Inquiry sent and linked to your event! Track all responses in your Event Dashboard.');
        } else {
          toast.success('Inquiry sent successfully! The service provider will contact you soon.');
        }
      }
      setInquiryOpen(false);
      setSelectedServices([]);
      
      // Reset form - but preserve event data for future inquiries from same event
      if (eventId && returnToEvent && eventData) {
        // Keep event-based pre-fills, just clear the message
        setInquiryData(prev => ({
          ...prev,
          message: ''
        }));
      } else {
        // Full reset for non-event inquiries
        setInquiryData({
          event_type: '',
          event_date: '',
          event_time: '',
          location: '',
          guest_count: '',
          budget_range: '',
          message: ''
        });
        // Also reset selected event
        setSelectedEventId(null);
        setEventData(null);
      }
      
      // If browsing from an event, return to the event page
      if (eventId && returnToEvent) {
        navigate(`/events/${eventId}?refresh=true`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send inquiry');
    } finally {
      setSubmittingInquiry(false);
      submittingInquiryRef.current = false;
    }
  };

  const handleChange = (e) => {
    setInquiryData({ ...inquiryData, [e.target.name]: e.target.value });
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareTitle = `${vendor.business_name} on CeleyApp`;
    const locationText = vendor.city ? `${vendor.city}, ${vendor.state}` : `${vendor.state} ${vendor.zip_code || ''}`.trim();
    const shareText = `Check out ${vendor.business_name} on CeleyApp! ${vendor.categories?.slice(0, 2).join(', ')} in ${locationText}`;

    // Try Web Share API first (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl
        });
        toast.success('Shared successfully!');
      } catch (error) {
        if (error.name !== 'AbortError') {
          copyToClipboard(shareUrl);
        }
      }
    } else {
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  const shareToSocial = async (platform) => {
    const shareUrl = encodeURIComponent(window.location.href);
    const shareText = encodeURIComponent(`Check out ${vendor.business_name} on CeleyApp!`);
    
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`,
      whatsapp: `https://wa.me/?text=${shareText}%20${shareUrl}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`
    };

    if (urls[platform]) {
      await openShareUrl(urls[platform]);
    }
  };

  if (loading) {
    return <CeleyLoader testid="vendor-detail-loader" />;
  }

  if (!vendor) {
    return null;
  }

  // Hero image: vendor-selected cover → first valid portfolio photo → profile photo
  const heroImageRaw = getHeroImage(vendor);
  const heroImage = heroImageRaw ? getImageUrl(heroImageRaw) : null;
  const isFoundingProvider = vendorBadges.some(b => b.badge_id === 'founding_provider' || b.name?.toLowerCase().includes('founding'));

  // Format price for display: always show "Starting at $X"
  const formatPrice = (price) => {
    if (!price) return 'Request pricing';
    const str = Array.isArray(price) && price.length > 0 ? price.join(', ') : (typeof price === 'string' ? price : '');
    if (!str) return 'Request pricing';
    // Range: "$500 - $1000" → "Starting at $500"
    const rangeMatch = str.match(/\$?([\d,]+)\s*[-–]\s*\$?([\d,]+)/);
    if (rangeMatch) return `Starting at $${rangeMatch[1]}`;
    // "Under $500" → "Starting at $500"
    const underMatch = str.match(/^Under\s*\$?([\d,]+)/i);
    if (underMatch) return `Starting at $${underMatch[1]}`;
    // Bare number: "500" → "Starting at $500"
    const numMatch = str.match(/^\$?([\d,]+)$/);
    if (numMatch) return `Starting at $${numMatch[1]}`;
    // Already has dollar sign or text like "$500+" → prefix
    if (str.startsWith('$')) return `Starting at ${str}`;
    return str;
  };

  // Raw price without "Starting at" prefix — for services/packages that have their own label
  const formatRawPrice = (price) => {
    const full = formatPrice(price);
    return full.replace(/^Starting at\s*/i, '');
  };

  return (
    <div className="min-h-screen-safe bg-[#FAFAF8] flex flex-col" data-testid="vendor-detail-page">
      {/* Event Context Banner */}
      {eventId && returnToEvent && (
        <div className="bg-emerald-50 border-b border-emerald-100 z-50 relative">
          <div className="max-w-3xl mx-auto px-4 py-2">
            <div className="flex items-center justify-between">
              <button onClick={() => navigate(`/events/${eventId}`)} className="flex items-center gap-2 text-emerald-700 hover:text-emerald-800 text-sm font-medium">
                <ArrowLeft className="w-4 h-4" /> Back to Event
              </button>
              <span className="text-xs text-emerald-600">Send inquiry to add to your event</span>
            </div>
          </div>
        </div>
      )}

      {/* Plan Event Context Banner */}
      {fromPlan && !eventId && (
        <div className="sticky top-0 bg-white border-b border-gray-200 z-50" data-testid="plan-context-banner">
          <div className="max-w-3xl mx-auto px-4 py-2.5">
            <div className="flex items-center justify-between">
              <button onClick={() => navigate('/plan-event')} className="flex items-center gap-2 text-emerald-700 hover:text-emerald-800 text-sm font-medium" data-testid="back-to-plan-btn">
                <ArrowLeft className="w-4 h-4" /> Back to Plan
              </button>
              {planEventType && (
                <span className="text-xs font-medium text-gray-500">Planning: <span className="text-emerald-600">{planEventType}</span></span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Back Navigation — universal fallback when no specific context banner is active */}
      {!eventId && !fromPlan && (
        <div className="sticky top-0 bg-white border-b border-gray-200 z-50" data-testid="back-nav-bar">
          <div className="max-w-3xl mx-auto px-4 py-2.5">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          </div>
        </div>
      )}

      {/* Owner Preview Mode — only visible to the authenticated vendor owner
          viewing their own unpublished profile. Replaces the generic
          "inactive listing" banner for the owner so the message is friendly
          and self-explanatory. Public/non-owner callers never see this. */}
      {vendor.is_owner_preview && (
        <div className="bg-emerald-50 border-b border-emerald-200 z-50 relative" data-testid="owner-preview-banner">
          <div className="max-w-3xl mx-auto px-4 py-2.5">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-emerald-700 flex-shrink-0" />
              <p className="text-xs sm:text-sm text-emerald-900">
                <span className="font-semibold">Preview Mode</span>
                <span className="hidden sm:inline"> — </span>
                <span className="block sm:inline text-emerald-800">your profile is not live yet</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Inactive Listing — public/non-owner view of a not-live vendor. The
          owner preview banner above replaces this for the owner. */}
      {!vendor.is_owner_preview && (vendor.vendor_active === false || vendor.admin_visible === false) && (
        <div className="bg-amber-50 border-b border-amber-200 z-50 relative">
          <div className="max-w-3xl mx-auto px-4 py-3">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-800"><span className="font-medium">This listing is currently inactive</span></p>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto w-full flex-1 px-4 pt-4 pb-24 md:pb-16">
        {/* ═══ HERO CARD ═══ */}
        <div className="bg-white rounded-2xl border border-[#ECECEA] overflow-hidden shadow-sm" data-testid="vendor-hero-card">
          {/* Hero image */}
          {heroImage ? (
            <div className="w-full aspect-[16/9] max-h-[220px] bg-gray-100 overflow-hidden cursor-pointer" onClick={() => openLightbox(heroImage)}>
              <img src={heroImage} alt={vendor.business_name} className="w-full h-full object-cover" onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
            </div>
          ) : null}

          {/* Card content */}
          <div className="p-4">
            {/* Top row: avatar + name + share */}
            <div className="flex items-start gap-3.5">
              {(() => {
                // Resolve once so delayMs and click handler agree on the same source.
                // `avatarSrc` is the raw URL (or null); `avatarUrl` is the CDN-signed
                // URL we feed the <img>. Keeping the fallback priority unchanged:
                // vendor.profile_photo → owner_photo → initial. Bug fix for the
                // Radix Avatar flash — see VendorDetail avatar loading-smoothness
                // spec (Feb 2026).
                const avatarSrc = getVendorAvatar(vendor);
                const avatarUrl = avatarSrc ? getImageUrl(avatarSrc) : undefined;
                return (
              <Avatar
                className="h-14 w-14 shrink-0 cursor-pointer border-2 border-white shadow-md bg-gray-100"
                onClick={() => { if (avatarSrc) openLightbox(avatarUrl); }}
              >
                <AvatarImage src={avatarUrl} alt={vendor.business_name} className="object-cover" />
                <AvatarFallback delayMs={avatarSrc ? 600 : 0} className="bg-emerald-100 text-emerald-700 text-lg font-bold">{vendor.business_name.charAt(0)}</AvatarFallback>
              </Avatar>
                );
              })()}

              <div className="flex-1 min-w-0">
                <h1 className="text-lg font-bold text-[#1A1A1A] leading-tight" data-testid="vendor-name">{vendor.business_name}</h1>
                {averageRating && (
                  <div className="flex items-center gap-1 mt-1">
                    <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                    <span className="text-[13px] font-semibold text-[#1A1A1A]">{averageRating}</span>
                    <span className="text-[12px] text-[#8A8A82]">({reviews.length})</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={() => setShareDialogOpen(true)} className="h-8 w-8 rounded-full bg-[#F5F5F3] flex items-center justify-center hover:bg-gray-200 transition-colors" data-testid="share-btn">
                  <Share2 className="h-4 w-4 text-[#6F6F6F]" />
                </button>
              </div>
            </div>

            {/* Tagline — full width below avatar row */}
            {vendor.short_tagline && (
              <p className="text-[13px] text-[#6F6F6F] mt-2 leading-relaxed line-clamp-3 break-words" data-testid="vendor-tagline">{vendor.short_tagline}</p>
            )}

            {/* Trust chips */}
            <div className="flex flex-wrap items-center gap-1.5 mt-3">
              {vendor.profile_strength_badge === 'ready_to_book' && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-[11px] font-medium text-emerald-700 border border-emerald-200" data-testid="badge-ready-to-book">
                  <CheckCircle className="h-3 w-3" /> Ready to Book
                </span>
              )}
              {vendor.profile_strength_badge === 'verified_provider' && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 text-[11px] font-medium text-blue-700 border border-blue-200" data-testid="badge-verified-provider">
                  <ShieldCheck className="h-3 w-3" /> Verified Provider
                </span>
              )}
              {isFoundingProvider && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F5F5F3] text-[11px] font-medium text-[#3A3A35]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Founding Provider
                </span>
              )}
              {vendorBadges.filter(b => !b.badge_id?.includes('founding') && !b.name?.toLowerCase().includes('founding')).length > 0 && (
                <div className="flex items-center">
                  <BadgeList badges={vendorBadges.filter(b => !b.badge_id?.includes('founding') && !b.name?.toLowerCase().includes('founding'))} size="sm" maxVisible={2} />
                </div>
              )}
              {vendor.response_time_hours ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F5F5F3] text-[11px] font-medium text-[#3A3A35]">
                  <Zap className="h-3 w-3 text-amber-500" /> Responds in {vendor.response_time_hours < 1 ? '<1 hr' : `${Math.round(vendor.response_time_hours)} hr`}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F5F5F3] text-[11px] font-medium text-[#3A3A35]">
                  <Zap className="h-3 w-3 text-amber-500" /> Responds quickly
                </span>
              )}
            </div>

            {/* Location + Price */}
            <div className="mt-3 pt-3 border-t border-[#F0F0ED] space-y-1.5">
              <div className="flex items-center gap-1.5 text-[13px] text-[#6F6F6F]">
                <MapPin className="h-3.5 w-3.5 text-[#9A9A94] shrink-0" />
                <span data-testid="vendor-location">{vendor.city ? `${vendor.city}, ` : ''}{vendor.state}{vendor.zip_code ? ` ${vendor.zip_code}` : ''}</span>
                <span className="text-[#D0D0CA]">&middot;</span>
                <span data-testid="vendor-radius">{getServiceAreaDisplay(vendor)}</span>
              </div>
              <div className="flex items-center gap-1 text-[13px] font-semibold text-emerald-600" data-testid="vendor-price">
                {/* Phase-5-alignment: single source of truth for the
                    "Starting at $…" line — see utils/pricing.js. Packages
                    first, then bookable à-la-carte (add-ons excluded),
                    finally the stored bucket. Consistent across every
                    surface a customer sees. The dollar-sign icon was
                    removed to avoid a visual "$$" when the label already
                    includes a currency symbol. */}
                <span>{formatVendorStartingPrice(vendor, formatPrice)}</span>
              </div>
              {reviews.length > 0 && (
                <p className="text-[12px] text-[#8A8A82] flex items-center gap-1" data-testid="vendor-trust-highlight">
                  <CalendarCheck className="h-3 w-3 text-emerald-500 shrink-0" />
                  {reviews.length}+ happy customer{reviews.length !== 1 ? 's' : ''} served
                </p>
              )}
            </div>

            {/* CTA row inside card */}
            <div className="flex items-center gap-2.5 mt-4" ref={headerCtaRef}>
              <Button
                size="default"
                className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-9 px-6 font-semibold text-[13px] shadow-sm max-w-[220px]"
                data-testid="request-quote-btn-hero"
                onClick={() => {
                  if (!user) { toast.error('Please sign in to send inquiries'); saveAuthRedirect(); navigate('/auth'); return; }
                  if (activeRole === 'vendor') { toast.error('Switch to Customer mode to send inquiries.'); return; }
                  setSelectedServices([]);
                  setInquiryOpen(true);
                }}
              >
                <MessageCircle className="h-4 w-4 mr-1.5" /> Request Quote
              </Button>
              {existingInquiry && (
                <Button
                  size="default"
                  variant="outline"
                  className="rounded-full h-9 px-5 font-semibold text-[13px] border-[#D0D0CA]"
                  data-testid="chat-vendor-btn-hero"
                  onClick={() => navigate('/customer-dashboard?tab=messages')}
                >
                  <MessageSquareText className="h-4 w-4 mr-1.5" /> Chat
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* ═══ TABS ═══ */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mt-3">
          <div className="sticky top-0 z-30 bg-[#FAFAF8]" data-testid="vendor-tabs">
            <div className="bg-white rounded-xl border border-[#ECECEA] overflow-hidden">
              <TabsList className="w-full h-10 bg-transparent p-0 gap-0 rounded-none">
                {['Overview', 'Reviews', 'Gallery', 'Services'].map(label => (
                  <TabsTrigger
                    key={label.toLowerCase()}
                    value={label.toLowerCase()}
                    data-testid={`tab-${label.toLowerCase()}`}
                    className="flex-1 h-full rounded-none border-b-[2.5px] border-transparent text-[13px] font-medium text-[#9A9A94] transition-all data-[state=active]:border-emerald-600 data-[state=active]:text-emerald-700 data-[state=active]:bg-transparent data-[state=active]:shadow-none hover:text-[#6F6F6F]"
                  >
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
          </div>

          {/* ═══ OVERVIEW TAB ═══ */}
          <TabsContent value="overview" className="mt-3 space-y-3" data-testid="tab-content-overview">
            {/* About */}
            {vendor.bio && (
              <BioSection bio={vendor.bio} />
            )}

            {/* Specialties */}
            {specialtyChips.length > 0 && (
              <div className="bg-white rounded-2xl border border-[#ECECEA] p-4" data-testid="specialty-chips">
                <h3 className="text-[14px] font-bold text-[#1A1A1A] mb-2.5">Specialties</h3>
                <div className="flex flex-wrap gap-2">
                  {specialtyChips.map(chip => (
                    <span key={chip.label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F5F5F3] text-[12px] font-medium text-[#3A3A35]">
                      <span>{chip.icon}</span> {chip.label}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Social links */}
            {(vendor.website || vendor.instagram || vendor.facebook) && (
              <div className="bg-white rounded-2xl border border-[#ECECEA] p-4" data-testid="social-links">
                <h3 className="text-[14px] font-bold text-[#1A1A1A] mb-2.5">Connect</h3>
                <div className="flex gap-2">
                  {vendor.website && (
                    <a href={vendor.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3.5 py-2 rounded-full border border-[#E0E0DC] hover:border-emerald-300 hover:bg-emerald-50 transition-colors text-[13px] text-[#4A4A45]">
                      <Globe className="h-4 w-4" /> Website
                    </a>
                  )}
                  {vendor.instagram && (
                    <a href={`https://instagram.com/${vendor.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3.5 py-2 rounded-full border border-[#E0E0DC] hover:border-pink-300 hover:bg-pink-50 transition-colors text-[13px] text-[#4A4A45]">
                      <Instagram className="h-4 w-4" /> Instagram
                    </a>
                  )}
                  {vendor.facebook && (
                    <a href={vendor.facebook.startsWith('http') ? vendor.facebook : `https://facebook.com/${vendor.facebook}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3.5 py-2 rounded-full border border-[#E0E0DC] hover:border-blue-300 hover:bg-blue-50 transition-colors text-[13px] text-[#4A4A45]">
                      <Facebook className="h-4 w-4" /> Facebook
                    </a>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          {/* ═══ REVIEWS TAB ═══ */}
          <TabsContent value="reviews" className="mt-3" data-testid="tab-content-reviews">
            {reviews.length === 0 ? (
              <div className="bg-white rounded-2xl border border-[#ECECEA] py-16 px-4 text-center" data-testid="no-reviews">
                <MessageSquareText className="h-12 w-12 mx-auto mb-3 text-gray-200" />
                <p className="text-[#4A4A45] text-[14px] font-semibold">No reviews yet</p>
                <p className="text-[#8A8A82] text-[13px] mt-1">Reviews from completed bookings will appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Rating overview */}
                <div className="bg-white rounded-2xl border border-[#ECECEA] p-4" data-testid="rating-overview">
                  <div className="flex items-start gap-5">
                    <div className="text-center shrink-0">
                      <div className="text-[40px] font-bold text-[#1A1A1A] leading-none">{averageRating}</div>
                      <div className="flex gap-0.5 mt-1.5 justify-center">
                        {[1, 2, 3, 4, 5].map(s => (
                          <Star key={s} className={`h-3.5 w-3.5 ${s <= Math.round(Number(averageRating)) ? 'fill-amber-400 text-amber-400' : 'text-gray-200'}`} />
                        ))}
                      </div>
                      <p className="text-[11px] text-[#8A8A82] mt-1">{reviews.length} review{reviews.length !== 1 ? 's' : ''}</p>
                    </div>
                    <div className="flex-1 space-y-1.5 pt-1">
                      {[5, 4, 3, 2, 1].map(star => {
                        const count = ratingBreakdown[star];
                        const pct = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
                        return (
                          <div key={star} className="flex items-center gap-2">
                            <span className="text-[11px] text-[#6F6F6F] w-3 text-right font-medium">{star}</span>
                            <Star className="h-3 w-3 text-amber-400 fill-amber-400 shrink-0" />
                            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-[11px] text-[#8A8A82] w-5 text-right">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Filter chips */}
                {reviewEventTypes.length > 0 && (
                  <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" data-testid="review-filters">
                    <button onClick={() => setReviewFilter('all')} className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold whitespace-nowrap transition-colors shrink-0 ${reviewFilter === 'all' ? 'bg-emerald-600 text-white' : 'bg-white text-[#4A4A45] border border-[#ECECEA] hover:bg-gray-50'}`}>
                      All ({reviews.length})
                    </button>
                    {reviewEventTypes.map(et => (
                      <button key={et} onClick={() => setReviewFilter(et)} className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold whitespace-nowrap transition-colors shrink-0 ${reviewFilter === et ? 'bg-emerald-600 text-white' : 'bg-white text-[#4A4A45] border border-[#ECECEA] hover:bg-gray-50'}`}>
                        {et}
                      </button>
                    ))}
                  </div>
                )}

                {/* Reviews list */}
                <div className="bg-white rounded-2xl border border-[#ECECEA] divide-y divide-gray-100" data-testid="review-list">
                  {filteredReviews.map((review) => (
                    <div key={review.id} className="p-4" data-testid={`review-${review.id}`}>
                      <div className="flex items-start gap-3">
                        <Avatar className="h-9 w-9 shrink-0">
                          <AvatarFallback className="bg-[#F5F5F3] text-[#6F6F6F] text-[13px] font-semibold">{(review.customer_name || 'U').charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-semibold text-[13px] text-[#1A1A1A]">{review.customer_name}</p>
                              <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-[#8A8A82]">
                                {review.event_type && <span className="text-[#6F6F6F]">{review.event_type}</span>}
                                {review.event_type && <span>&middot;</span>}
                                <span>{new Date(review.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                              </div>
                            </div>
                            {renderStars(review.rating)}
                          </div>
                          <p className="text-[13px] text-[#4A4A45] leading-relaxed mt-2">{review.comment}</p>
                          {review.images && review.images.length > 0 && (
                            <div className="flex gap-2 mt-3 overflow-x-auto">
                              {review.images.map((image, imgIdx) => (
                                <div key={imgIdx} className="h-16 w-16 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity shrink-0 bg-gray-100" onClick={() => openReviewLightbox(review.images, imgIdx)}>
                                  <img src={getImageUrl(image)} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* ═══ GALLERY TAB ═══ */}
          <TabsContent value="gallery" className="mt-3" data-testid="tab-content-gallery">
            {validPhotos.length === 0 ? (
              <div className="bg-white rounded-2xl border border-[#ECECEA] py-16 px-4 text-center" data-testid="no-gallery">
                <Camera className="h-12 w-12 mx-auto mb-3 text-gray-200" />
                <p className="text-[#4A4A45] text-[14px] font-semibold">No portfolio photos yet</p>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-2.5 mb-3 bg-white rounded-xl p-3 border border-[#ECECEA]">
                  <Sparkles className="h-4 w-4 text-amber-500 shrink-0" />
                  <div>
                    <p className="text-[13px] font-semibold text-[#1A1A1A]">Real events, real setups</p>
                    <p className="text-[11px] text-[#8A8A82]">Photos from actual events by {vendor.business_name}.</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-1.5" data-testid="gallery-grid">
                  {validPhotos.map((photo, idx) => (
                    <div key={idx} className="aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer hover:opacity-90 transition-opacity" data-testid={`gallery-photo-${idx}`} onClick={() => openLightbox(getImageUrl(photo))}>
                      <img src={getImageUrl(photo)} alt={`Portfolio ${idx + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full text-gray-300 text-[10px]">Unavailable</div>'; }} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* ═══ SERVICES TAB ═══ */}
          <TabsContent value="services" className="mt-3 space-y-3" data-testid="tab-content-services">
            {/* Pricing note */}
            <div className="flex items-start gap-3 bg-white rounded-xl p-3.5 border border-[#ECECEA]" data-testid="pricing-disclaimer">
              <Tag className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
              <p className="text-[12px] text-[#6F6F6F] leading-relaxed">Pricing shown is approximate. Final quote depends on event details and customization.</p>
            </div>

            {/* Tab toggle: Packages / À La Carte — only if both exist */}
            {packages.length > 0 && individualServices.length > 0 && (
              <div className="flex gap-1 p-1 bg-gray-100 rounded-lg" data-testid="services-toggle">
                <button
                  onClick={() => setServicesViewTab('all')}
                  className={`flex-1 text-xs font-semibold py-2 rounded-md transition-colors ${servicesViewTab === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >All</button>
                <button
                  onClick={() => setServicesViewTab('packages')}
                  className={`flex-1 text-xs font-semibold py-2 rounded-md transition-colors ${servicesViewTab === 'packages' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >Packages</button>
                <button
                  onClick={() => setServicesViewTab('services')}
                  className={`flex-1 text-xs font-semibold py-2 rounded-md transition-colors ${servicesViewTab === 'services' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >À La Carte</button>
              </div>
            )}

            {/* ── PACKAGES ── */}
            {packages.length > 0 && (servicesViewTab === 'all' || servicesViewTab === 'packages') && (
              <div className="space-y-3" data-testid="packages-section">
                {packages.length > 0 && individualServices.length > 0 && servicesViewTab === 'all' && (
                  <p className="text-[13px] font-bold text-[#1A1A1A]">Packages</p>
                )}
                {[...packages].sort((a, b) => (b.is_most_popular ? 1 : 0) - (a.is_most_popular ? 1 : 0)).map((pkg, idx) => {
                  const isSelected = selectedServices.includes(pkg.name);
                  return (
                  <div 
                    key={pkg.name || idx} 
                    className={`bg-white rounded-2xl border p-4 cursor-pointer transition-all ${isSelected ? 'border-emerald-500 ring-2 ring-emerald-200 bg-emerald-50/30' : pkg.is_most_popular ? 'border-emerald-300 ring-1 ring-emerald-100' : 'border-[#ECECEA]'}`}
                    onClick={() => toggleServiceSelection(pkg.name)}
                    data-testid={`package-item-${idx}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Selection checkbox */}
                      <div className={`mt-0.5 h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'bg-emerald-600 border-emerald-600' : 'border-gray-300'}`} data-testid={`package-select-${idx}`}>
                        {isSelected && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                    {pkg.is_most_popular && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2.5 py-0.5 rounded-full bg-emerald-600 text-white font-bold mb-2" data-testid="most-popular-badge">
                        <Star className="w-2.5 h-2.5 fill-white" /> MOST POPULAR
                      </span>
                    )}
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-[15px] text-[#1A1A1A]">{pkg.name}</h4>
                        {pkg.description && <p className="text-[12px] text-[#6F6F6F] mt-1 leading-relaxed">{pkg.description}</p>}
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        {pkg.starting_price ? (
                          <>
                            <p className="text-[10px] text-[#8A8A82]">Starting at</p>
                            <p className="text-[17px] font-bold text-emerald-600">{typeof pkg.starting_price === 'number' ? `$${pkg.starting_price}` : formatRawPrice([pkg.starting_price])}</p>
                          </>
                        ) : (
                          <p className="text-[12px] text-[#8A8A82]">Request pricing</p>
                        )}
                      </div>
                    </div>

                    {/* What's Included */}
                    {pkg.includes && pkg.includes.length > 0 && (
                      <PackageIncludes items={pkg.includes} />
                    )}

                    {/* Duration */}
                    {pkg.duration && (
                      <div className="mt-3 pt-3 border-t border-gray-50">
                        <span className="flex items-center gap-1 text-[12px] text-[#8A8A82]">
                          <Clock className="h-3.5 w-3.5" /> {pkg.duration}
                        </span>
                      </div>
                    )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}

            {/* ── INDIVIDUAL SERVICES (À La Carte) ── */}
            {individualServices.length > 0 && (servicesViewTab === 'all' || servicesViewTab === 'services') && (
              <div className="space-y-2" data-testid="services-section">
                {packages.length > 0 && servicesViewTab === 'all' && (
                  <p className="text-[13px] font-bold text-[#1A1A1A] mt-2">À La Carte Services</p>
                )}
                {[...individualServices].sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0)).map((svc, idx) => {
                  const isSelected = selectedServices.includes(svc.name);
                  return (
                  <div 
                    key={svc.name || idx} 
                    className={`bg-white rounded-xl border p-3.5 cursor-pointer transition-all ${isSelected ? 'border-emerald-500 ring-2 ring-emerald-200 bg-emerald-50/30' : svc.is_primary ? 'border-emerald-200' : 'border-[#ECECEA]'}`}
                    onClick={() => toggleServiceSelection(svc.name)}
                    data-testid={`service-item-${idx}`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Selection checkbox */}
                      <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'bg-emerald-600 border-emerald-600' : 'border-gray-300'}`} data-testid={`service-select-${idx}`}>
                        {isSelected && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-[14px] text-[#1A1A1A]">{svc.name}</h4>
                          {svc.is_primary && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-600 text-white font-semibold">Primary</span>}
                        </div>
                        {svc.description && <p className="text-[12px] text-[#6F6F6F] mt-0.5">{svc.description}</p>}
                        {svc.duration && (
                          <span className="flex items-center gap-1 text-[11px] text-[#8A8A82] mt-1">
                            <Clock className="h-3 w-3" /> {svc.duration}
                          </span>
                        )}
                      </div>
                      <div className="text-right shrink-0 ml-2">
                        {svc.starting_price ? (
                          <>
                            <p className="text-[10px] text-[#8A8A82]">Starting at</p>
                            <p className="text-[14px] font-bold text-emerald-600">{typeof svc.starting_price === 'number' ? `$${svc.starting_price}` : formatRawPrice([svc.starting_price])}</p>
                          </>
                        ) : (
                          <p className="text-[12px] text-[#8A8A82]">Request pricing</p>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}

                {/* Custom quote prompt */}
                <div className="bg-emerald-50/50 rounded-xl border border-emerald-100 p-3.5 text-center" data-testid="custom-quote-prompt">
                  <p className="text-[13px] font-semibold text-[#1A1A1A]">Don&apos;t see what you need?</p>
                  <p className="text-[11px] text-[#6F6F6F] mt-0.5">We offer custom services. Let&apos;s discuss your vision.</p>
                  <button
                    onClick={() => { if (!user) { toast.error('Please sign in'); saveAuthRedirect(); navigate('/auth'); return; } setSelectedServices([]); setInquiryOpen(true); }}
                    className="text-[12px] text-emerald-600 font-semibold mt-1.5 hover:text-emerald-700"
                  >
                    Request Custom Quote &rarr;
                  </button>
                </div>
              </div>
            )}

            {/* Booking details */}
            <div className="bg-white rounded-2xl border border-[#ECECEA] p-4" data-testid="booking-rules">
              <h3 className="text-[14px] font-bold text-[#1A1A1A] mb-2.5">Booking Details</h3>
              <div className="space-y-2.5">
                <div className="flex items-center gap-3 text-[13px] text-[#4A4A45]">
                  <MapPin className="h-4 w-4 text-[#9A9A94] shrink-0" />
                  <span>{getServiceAreaDisplay(vendor)}</span>
                </div>
                {vendor.allow_same_day && (
                  <div className="flex items-center gap-3 text-[13px] text-[#4A4A45]">
                    <Clock className="h-4 w-4 text-emerald-500 shrink-0" />
                    <span>Same-day bookings accepted</span>
                  </div>
                )}
                {vendor.allow_next_day && (
                  <div className="flex items-center gap-3 text-[13px] text-[#4A4A45]">
                    <Clock className="h-4 w-4 text-blue-500 shrink-0" />
                    <span>Next-day bookings accepted</span>
                  </div>
                )}
                {vendor.min_notice_hours > 0 && (
                  <div className="flex items-center gap-3 text-[13px] text-[#4A4A45]">
                    <CalendarCheck className="h-4 w-4 text-[#9A9A94] shrink-0" />
                    <span>Minimum {Math.ceil(vendor.min_notice_hours / 24)} day{Math.ceil(vendor.min_notice_hours / 24) > 1 ? 's' : ''} advance notice</span>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* ═══ Sticky Bottom CTA (mobile only for general, all viewports for selection) ═══ */}
      {!fromPlan && (
      <div
        className={`fixed left-0 right-0 z-40 transition-all duration-200 ease-out ${selectedServices.length > 0 ? '' : 'md:hidden'} ${showStickyCta || selectedServices.length > 0 ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'}`}
        style={{ bottom: user ? '64px' : '0' }}
        data-testid="sticky-cta"
      >
        <div className={`mx-auto px-4 py-3 ${selectedServices.length > 0 ? 'max-w-xl md:pb-4' : 'bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]'}`}>
          <div className={`flex items-center gap-2.5 ${selectedServices.length > 0 ? 'bg-white/95 backdrop-blur-md rounded-full shadow-[0_4px_24px_rgba(0,0,0,0.12)] border border-gray-200 px-2 py-2' : ''}`}>
          {selectedServices.length > 0 ? (
            <>
              <Button size="default" className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-10 px-7 font-semibold text-[13px] shadow-sm flex-1" data-testid="request-selected-btn" onClick={openInquiryWithSelection}>
                <MessageCircle className="h-[18px] w-[18px] mr-2" /> Request selected ({selectedServices.length})
              </Button>
              <Button size="default" variant="outline" className="rounded-full h-10 px-4 font-semibold text-[13px] border-gray-300 text-gray-600" data-testid="clear-selection-btn" onClick={() => setSelectedServices([])}>
                <X className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button size="default" className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-10 px-7 font-semibold text-[13px] shadow-sm" data-testid="request-quote-btn" onClick={() => { if (!user) { toast.error('Please sign in to send inquiries'); saveAuthRedirect(); navigate('/auth'); return; } if (activeRole === 'vendor') { toast.error('Switch to Customer mode.'); return; } setSelectedServices([]); setInquiryOpen(true); }}>
                <MessageCircle className="h-[18px] w-[18px] mr-2" /> Request Quote
              </Button>
              {existingInquiry && (
                <Button size="default" variant="outline" className="rounded-full h-10 px-5 font-semibold text-[13px] border-[#D0D0CA]" data-testid="chat-vendor-btn-sticky" onClick={() => navigate('/customer-dashboard?tab=messages')}>
                  <MessageSquareText className="h-[18px] w-[18px] mr-1.5" /> Chat
                </Button>
              )}
            </>
          )}
        </div>
        </div>
      </div>
      )}

      {/* ═══ Sticky Bottom "Add to Plan" (from event builder) ═══ */}
      {fromPlan && (
        <div
          className="fixed left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-gray-200 px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
          style={{ bottom: user ? '64px' : '0' }}
          data-testid="plan-sticky-cta"
        >
          <div className="max-w-3xl mx-auto flex gap-2.5">
            <Button
              size="lg"
              className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700 h-11 font-semibold text-[14px] shadow-sm"
              data-testid="plan-add-btn"
              onClick={() => {
                toast.success(`${vendor.business_name} noted — return to your plan to finalize`);
                navigate('/plan-event');
              }}
            >
              <Plus className="h-[18px] w-[18px] mr-2" /> Add to Plan
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="rounded-full h-11 px-5 font-semibold text-[14px] border-[#D0D0CA]"
              onClick={() => navigate('/plan-event')}
            >
              <ArrowLeft className="h-[18px] w-[18px] mr-1.5" /> Back
            </Button>
          </div>
        </div>
      )}

      {/* ═══ Share Dialog ═══ */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Share {vendor.business_name}</DialogTitle>
            <DialogDescription>Share this provider with friends or on social media</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Profile Link</Label>
              <div className="flex gap-2 mt-2">
                <Input value={window.location.href} readOnly className="flex-1" />
                <Button variant="outline" onClick={() => copyToClipboard(window.location.href)}>{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</Button>
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Share on Social Media</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={() => shareToSocial('facebook')} className="w-full"><Facebook className="h-4 w-4 mr-2" />Facebook</Button>
                <Button variant="outline" onClick={() => shareToSocial('twitter')} className="w-full"><svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>X/Twitter</Button>
                <Button variant="outline" onClick={() => shareToSocial('whatsapp')} className="w-full"><MessageCircle className="h-4 w-4 mr-2" />WhatsApp</Button>
                <Button variant="outline" onClick={() => shareToSocial('linkedin')} className="w-full"><svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>LinkedIn</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ Inquiry ComposerSheet (Form-style) ═══ */}
      <ComposerSheet
        open={inquiryOpen}
        onClose={() => setInquiryOpen(false)}
        title={`Send Inquiry to ${vendor.business_name}`}
        subtitle="Fill out the form below and the service provider will contact you"
        maxWidth="sm:max-w-2xl"
        testId="inquiry-composer-sheet"
        footer={
          <Button type="button" onClick={handleInquirySubmit} disabled={submittingInquiry} className="w-full h-11 rounded-xl font-semibold" data-testid="send-inquiry-btn">{submittingInquiry ? 'Sending…' : 'Send Inquiry'}</Button>
        }
      >
        <div className="space-y-4">
          {selectedServices.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="selected-package-banner">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[11px] text-emerald-600 font-medium">Interested in ({selectedServices.length})</p>
                <button type="button" onClick={() => setSelectedServices([])} className="text-emerald-400 hover:text-emerald-600 p-1"><X className="h-4 w-4" /></button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {selectedServices.map((name) => (
                  <span key={name} className="inline-flex items-center gap-1 text-[12px] font-semibold text-emerald-800 bg-white/70 border border-emerald-200 rounded-md px-2 py-0.5">
                    {name}
                    <button type="button" onClick={() => setSelectedServices(prev => prev.filter(s => s !== name))} className="text-emerald-400 hover:text-emerald-700 ml-0.5"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            </div>
          )}
          {eventId && returnToEvent && eventData && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-sm font-medium text-emerald-800">Sending inquiry for: {eventData.event_type}</p>
              <p className="text-xs text-emerald-600 mt-0.5">{eventData.event_date && new Date(eventData.event_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}{eventData.location_city && ` \u2022 ${eventData.location_city}`}</p>
            </div>
          )}
          {!eventId && user && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-emerald-500" /> Attach to an Event <span className="text-gray-400 font-normal">(optional)</span></p>
                {selectedEventId && <button type="button" onClick={handleClearEventAttachment} className="text-xs text-gray-500 hover:text-gray-700">Clear</button>}
              </div>
              {loadingUserEvents ? (
                <div className="flex items-center gap-2 text-sm text-gray-500"><div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /> Loading...</div>
              ) : selectedEventId && eventData ? (
                <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-300 rounded-md p-3" data-testid="event-linked-card">
                  <span className="flex items-center justify-center h-9 w-9 rounded-full bg-emerald-500 text-white shrink-0">
                    <CheckCircle2 className="h-5 w-5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-emerald-800">Linked to: {eventData.event_type}</p>
                    <p className="text-xs text-emerald-700 mt-0.5">
                      {eventData.event_date && new Date(eventData.event_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      {eventData.location_city && ` \u2022 ${eventData.location_city}`}
                    </p>
                  </div>
                </div>
              ) : userEvents.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500">Tap an event to attach this inquiry:</p>
                  <div className="max-h-40 overflow-y-auto space-y-1.5">
                    {userEvents.slice(0, 5).map((event) => (
                      <button
                        key={event.id}
                        type="button"
                        onClick={() => handleSelectEvent(event)}
                        data-testid={`attach-event-${event.id}`}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md border border-gray-200 bg-white hover:border-emerald-400 hover:bg-emerald-50 active:scale-[0.98] transition-all group"
                      >
                        <span className="flex items-center justify-center h-9 w-9 rounded-full bg-emerald-50 border border-emerald-100 shrink-0 group-hover:bg-emerald-100">
                          <Sparkles className="h-4 w-4 text-emerald-500" />
                        </span>
                        <div className="flex-1 min-w-0 text-left">
                          <p className="text-sm font-medium text-gray-800 truncate">{event.event_type}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {event.event_date && new Date(event.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            {event.location_city && ` \u2022 ${event.location_city}`}
                          </p>
                        </div>
                        <span className="text-[11px] font-medium text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">Tap to attach</span>
                        <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-emerald-500 shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-2">
                  <p className="text-xs text-gray-500 mb-2">No active events found</p>
                  <Button type="button" variant="outline" size="sm" onClick={() => {
                    // Feb 2026: vendor-seeded planner entry (Mode B).
                    // PlanEvent reads these params once on mount and
                    // merges into eventData, then strips them from the
                    // URL. See audit memo + PlanEvent mount-effect.
                    setInquiryOpen(false);
                    const seedCategory = vendor.category || (Array.isArray(vendor.categories) ? vendor.categories[0] : null) || 'Service';
                    // Parse starting_price across the shapes the backend
                    // actually returns: number, string, or array-of-string.
                    // Mirrors PlanEvent.getVendorPrice (intentional local
                    // duplicate per audit 2026-02-28 — do not extract).
                    const rawPrice = Array.isArray(vendor.starting_price) ? vendor.starting_price[0] : vendor.starting_price;
                    let seedPrice = null;
                    if (typeof rawPrice === 'number' && rawPrice > 0) {
                      seedPrice = rawPrice;
                    } else if (typeof rawPrice === 'string' && rawPrice.length > 0) {
                      const m = rawPrice.match(/\$?([\d,]+)/);
                      if (m) {
                        const n = parseInt(m[1].replace(/,/g, ''), 10);
                        if (Number.isFinite(n) && n > 0) seedPrice = n;
                      }
                    }
                    const params = new URLSearchParams();
                    params.set('seedVendorId', vendor.id);
                    params.set('seedCategory', seedCategory);
                    params.set('seedVendorName', vendor.business_name || 'Vendor');
                    if (seedPrice !== null) params.set('seedStartingPrice', String(seedPrice));
                    // Carry the avatar fallback chain (profile_photo →
                    // owner_photo) so the seeded card on Step 4 can render
                    // the real vendor image instead of falling back to the
                    // first-letter initial. URLs are absolute S3 paths.
                    if (vendor.profile_photo) params.set('seedProfilePhoto', vendor.profile_photo);
                    if (vendor.owner_photo) params.set('seedOwnerPhoto', vendor.owner_photo);
                    // Carry over inquiry-form draft so typed values aren't lost.
                    // Message intentionally NOT carried — planner has no
                    // equivalent textarea and would silently drop it on save.
                    if (inquiryData.event_type) params.set('seedEventType', inquiryData.event_type);
                    if (inquiryData.event_date) params.set('seedEventDate', inquiryData.event_date);
                    if (inquiryData.event_time) params.set('seedEventTime', inquiryData.event_time);
                    if (inquiryData.location) params.set('seedLocation', inquiryData.location);
                    if (inquiryData.guest_count) params.set('seedGuestCount', String(inquiryData.guest_count));
                    if (inquiryData.budget_range) {
                      // Map "$250-$500" / "Under $250" / "$1000+" → first $ value
                      const m = String(inquiryData.budget_range).match(/\$\s*(\d+)/);
                      if (m) params.set('seedBudget', m[1]);
                    }
                    navigate(`/plan-event?${params.toString()}`);
                  }} className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Create event</Button>
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="event_type">Event Type *</Label>
              <Select value={inquiryData.event_type} onValueChange={(v) => setInquiryData({ ...inquiryData, event_type: v })} required>
                <SelectTrigger data-testid="inquiry-event-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {(eventTypeOptions.length > 0 ? eventTypeOptions : DIRECT_INQUIRY_FALLBACK_TYPES)
                    .filter((t) => t !== OTHER_EVENT_TYPE)
                    .map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  <SelectItem value={OTHER_EVENT_TYPE}>{OTHER_EVENT_TYPE}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="event_date">Event Date *</Label>
              <Input id="event_date" name="event_date" type="date" value={inquiryData.event_date} onChange={handleChange} min={getMinDate()} required data-testid="inquiry-event-date" />
              {inquiryData.event_date && !validateBookingDate(inquiryData.event_date).valid && (<p className="text-sm text-red-600 mt-1">{validateBookingDate(inquiryData.event_date).error}</p>)}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label htmlFor="event_time">Event Time</Label><TimePicker id="event_time" name="event_time" value={inquiryData.event_time} onChange={handleChange} data-testid="inquiry-event-time" /></div>
            <div><Label htmlFor="guest_count">Guest Count</Label><Input id="guest_count" name="guest_count" type="number" inputMode="numeric" pattern="[0-9]*" placeholder="e.g., 50" value={inquiryData.guest_count} onChange={handleChange} data-testid="inquiry-guest-count" /></div>
          </div>
          <div><Label htmlFor="location">Event Location *</Label><Input id="location" name="location" placeholder="City, State or Full Address" value={inquiryData.location} onChange={handleChange} required data-testid="inquiry-location" /></div>
          <div>
            <Label htmlFor="budget_range">Budget Range</Label>
            <Select value={inquiryData.budget_range} onValueChange={(v) => setInquiryData({ ...inquiryData, budget_range: v })}>
              <SelectTrigger data-testid="inquiry-budget"><SelectValue placeholder="Select budget" /></SelectTrigger>
              <SelectContent><SelectItem value="Under $250">Under $250</SelectItem><SelectItem value="$250-$500">$250-$500</SelectItem><SelectItem value="$500-$1000">$500-$1000</SelectItem><SelectItem value="$1000+">$1000+</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label htmlFor="message">Message *</Label><Textarea id="message" name="message" rows={4} placeholder="Tell the provider about your event..." value={inquiryData.message} onChange={handleChange} required data-testid="inquiry-message" /></div>
        </div>
      </ComposerSheet>

      {/* Image Lightbox */}
      {lightboxOpen && lightboxImages.length > 0 && (
        <ImageLightbox
          images={lightboxImages}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      {user && <BottomTabBar activeTab="browse" variant="customer" />}
      {!user && <Footer />}
    </div>
  );
};

export default VendorDetail;
